import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { workflowsService, plansService } from 'services';
import { wfType, wfStatus } from 'services/workflowsService';
import Loading from 'components/Loading';
import moment from 'moment';
import 'moment/locale/es';
import { OverlayTrigger, Tooltip, Dropdown } from "react-bootstrap";
import { Link } from 'react-router-dom';
import { FiMoreHorizontal } from "react-icons/fi";
import Avatar from 'components/Avatar';
import {
    isEmployeeVisibleInTeamAbsences,
} from 'utils/employeePeriod';

class TeamAbsences extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            allEmployees: [],
            displayedEmployees: [],
            page: 0,
            pageSize: 20,
            hasMore: true,
            absences: [],
            absenceMap: {},
            days: []
        };
        this.scrollRef = React.createRef();
        this.fetchLock = false;
    }

    componentDidMount() {
        this.initializeData();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.year !== this.props.year ||
            prevProps.month !== this.props.month ||
            prevProps.group_id !== this.props.group_id ||
            prevProps.employees !== this.props.employees ||
            prevProps.refreshKey !== this.props.refreshKey) {
            this.initializeData();
        }
    }

    initializeData = () => {
        this.fetchLock = false;
        const { employees, group_id, year, month } = this.props;
        const groupScopedList = Boolean(group_id) || this.props.groupScopedList;
        let filteredEmployees = (employees || []).filter((employee) =>
            isEmployeeVisibleInTeamAbsences(
                employee,
                year,
                month,
                group_id,
                groupScopedList
            )
        );

        const uniqueEmployees = [];
        const seenIds = new Set();
        filteredEmployees.forEach(emp => {
            const idKey = emp?.id === undefined || emp?.id === null ? null : String(emp.id);
            if (idKey !== null && !seenIds.has(idKey)) {
                seenIds.add(idKey);
                uniqueEmployees.push(emp);
            }
        });

        this.setState({
            allEmployees: uniqueEmployees,
            displayedEmployees: [],
            page: 0,
            hasMore: true,
            absences: [],
            absenceMap: {},
            loading: false
        }, () => {
            this.loadMoreData();
        });
    }

    loadMoreData = async () => {
        if (this.fetchLock || this.state.loading) return;
        this.fetchLock = true;

        const { allEmployees, displayedEmployees, page, pageSize } = this.state;
        const nextEmployees = allEmployees.slice(page * pageSize, (page + 1) * pageSize);

        if (nextEmployees.length === 0) {
            this.setState({ hasMore: false });
            return;
        }

        this.setState({ loading: true });

        try {
            await this.fetchAbsencesForEmployees(nextEmployees);

            const dedupeEmployeesById = (list) => {
                const seen = new Set();
                return list.filter((emp) => {
                    const id = emp?.id;
                    if (id === undefined || id === null) return false;
                    const key = String(id);
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });
            };

            this.setState((prevState) => {
                const merged = [...prevState.displayedEmployees, ...nextEmployees];
                return {
                    displayedEmployees: dedupeEmployeesById(merged),
                    page: prevState.page + 1,
                    loading: false,
                    hasMore: (prevState.page + 1) * pageSize < allEmployees.length
                };
            });
        } finally {
            this.fetchLock = false;
        }
    }

    fetchAbsencesForEmployees = async (targetEmployees) => {
        const { year, month, group_id } = this.props;
        if (!targetEmployees || targetEmployees.length === 0) return;

        const startDate = moment([year, month - 1]);
        const endDate = moment(startDate).endOf('month');
        const start_from = startDate.format('YYYY-MM-DD');
        const start_to = endDate.format('YYYY-MM-DD');

        const employeeIds = targetEmployees.map(e => e.id);

        try {
            const promises = [];

            promises.push(workflowsService.getManagedAbsences({
                start_from,
                start_to,
                page_size: 1000
            }));

            const planOptions = {
                start: start_from,
                end: start_to,
                page_size: 1000,
                update_employee_ids: employeeIds
            };

            promises.push(plansService.getPlanAssignments(planOptions));

            const [workflowsRes, plansRes] = await Promise.all(promises);

            let newAbsences = [];

            if (workflowsRes.ok && workflowsRes.data) {
                workflowsRes.data.forEach(w => {
                    if ((w.workflow_type === wfType.absences || w.workflow_type === wfType.vacations) && w.workflow_status === wfStatus.approved) {
                        if (w.usage && w.usage.length > 0) {
                            w.usage.forEach(u => {
                                newAbsences.push({
                                    id: w.id,
                                    employee_id: w.employee.id,
                                    employee_name: w.employee.name + ' ' + (w.employee.surname || ''),
                                    date: u.day,
                                    status: w.workflow_status,
                                    type: w.workflow_type,
                                    color: (w.shift && (w.shift.background_color || w.shift.color)) || w.color,
                                    comments: w.comments,
                                    source: 'workflow',
                                    shift: w.shift,
                                    attachments: w.attachments,
                                    requester: w.employee
                                });
                            });
                        }
                    }
                });
            }

            if (plansRes.ok) {
                let assignments = [];
                if (Array.isArray(plansRes.data)) {
                    plansRes.data.forEach(item => {
                        const employeeId = (item.employee && item.employee.id) ? item.employee.id : (item.id || item.employee_id);
                        if (item.plans && Array.isArray(item.plans)) {
                            item.plans.forEach((p, index) => {
                                let planDate = startDate.clone().add(index, 'days').format('YYYY-MM-DD');
                                let assignmentsList = [];
                                if (Array.isArray(p)) {
                                    assignmentsList = p;
                                } else if (p && p.assignments && Array.isArray(p.assignments)) {
                                    assignmentsList = p.assignments;
                                    if (p.day) planDate = p.day;
                                }

                                if (assignmentsList.length > 0) {
                                    assignmentsList.forEach(a => {
                                        let date = null;
                                        if (a.shift) {
                                            date = a.shift.day || a.shift.date;
                                            if (!date && a.shift.start) date = a.shift.start.split('T')[0];
                                        }
                                        if (!date) date = a.date || a.start_date || (a.start ? a.start.split('T')[0] : null);
                                        if (!date && planDate) date = planDate;
                                        if (date && date.includes('T')) date = date.split('T')[0];

                                        const enrichedAssign = { ...a, employee_id: employeeId, date: date };
                                        assignments.push(enrichedAssign);
                                    });
                                }
                            });
                        } else if (item.shift) {
                            assignments.push(item);
                        }
                    });
                }

                assignments.forEach(assign => {
                    const s = assign.shift;
                    const isAbsence = s && (
                        (s.type && (s.type.includes('Absent') || s.type.includes('Vacation') || s.type.includes('Ausencia') || s.type.includes('Vacaciones'))) ||
                        (s.shift_type && (s.shift_type.includes('Absent') || s.shift_type.includes('Vacation') || s.shift_type.includes('Ausencia') || s.shift_type.includes('Vacaciones'))) ||
                        (s.name && (s.name.toLowerCase().includes('ausencia') || s.name.toLowerCase().includes('vacaciones')))
                    );

                    if (isAbsence) {
                        let color = s.color;
                        if (s.background_color) color = s.background_color;

                        const absenceObj = {
                            id: assign.id || assign.shift.id,
                            employee_id: assign.employee_id,
                            date: assign.date,
                            status: wfStatus.approved,
                            type: s.name,
                            color: color,
                            shift_color: s.background_color,
                            source: 'plan',
                            shift: s,
                            attachments: [],
                        };
                        newAbsences.push(absenceObj);
                    }
                });
            }

            this.setState(prevState => {
                const newMap = { ...prevState.absenceMap };
                newAbsences.forEach(a => {
                    const key = `${a.employee_id}_${a.date}`;
                    if (!newMap[key]) newMap[key] = [];
                    if (!newMap[key].some(existing => existing.id === a.id && existing.date === a.date)) {
                        newMap[key].push(a);
                    }
                });
                return {
                    absences: [...prevState.absences, ...newAbsences],
                    absenceMap: newMap
                };
            });

        } catch (e) {
            console.error("Error fetching usage for batch:", e);
        }
    }

    getDaysInMonth = () => {
        const { year, month } = this.props;
        const daysInMonth = moment([year, month - 1]).daysInMonth();
        let days = [];
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(moment([year, month - 1, i]));
        }
        return days;
    }

    getAbsenceForDay = (employee, day) => {
        const dateStr = day.format('YYYY-MM-DD');
        const key = `${employee.id}_${dateStr}`;
        const relevantAbsences = this.state.absenceMap[key] || [];

        if (relevantAbsences.length === 0) return null;

        let absence = relevantAbsences.find(a => a.source === 'plan');
        if (!absence) {
            absence = relevantAbsences.find(a => a.status === wfStatus.approved);
        }
        if (!absence) {
            absence = relevantAbsences[0];
        }

        if (absence.status === wfStatus.canceled) return null;

        return absence;
    }

    areSameAbsence = (a, b) => {
        if (!a || !b) return false;
        return a.type === b.type &&
            a.status === b.status &&
            a.source === b.source;
    }

    getAbsenceColor = (absence) => {
        if (!absence) return 'transparent';

        let bgColor = '#45E6B0';

        if (absence.color) {
            bgColor = absence.color;
        } else if (absence.status === wfStatus.pending) {
            bgColor = '#FABE28';
        } else if (absence.status === wfStatus.denied) {
            bgColor = '#FF4545';
        }

        return bgColor;
    }

    hexToRgba = (hex, alpha) => {
        let c;
        if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
            c = hex.substring(1).split('');
            if (c.length === 3) {
                c = [c[0], c[0], c[1], c[1], c[2], c[2]];
            }
            c = '0x' + c.join('');
            return 'rgba(' + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') + ',' + alpha + ')';
        }
        return hex;
    }

    renderRowCells = (employee, days) => {
        const { t } = this.props;

        return days.map((day, index) => {
            const dateStr = day.format('YYYY-MM-DD');
            const currentAbsence = this.getAbsenceForDay(employee, day);
            const prevAbsence = index > 0 ? this.getAbsenceForDay(employee, days[index - 1]) : null;

            if (currentAbsence && prevAbsence && this.areSameAbsence(currentAbsence, prevAbsence)) {
                return null;
            }

            let colSpan = 1;
            if (currentAbsence) {
                for (let i = index + 1; i < days.length; i++) {
                    const nextAbsence = this.getAbsenceForDay(employee, days[i]);
                    if (nextAbsence && this.areSameAbsence(currentAbsence, nextAbsence)) {
                        colSpan++;
                    } else {
                        break;
                    }
                }
            }

            if (currentAbsence) {
                const baseColor = this.getAbsenceColor(currentAbsence);
                const style = {
                    backgroundColor: this.hexToRgba(baseColor, 0.20),
                    border: `1px solid ${baseColor}`,
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: 'black',
                    fontWeight: '400',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    padding: '4px 6px',
                };

                const title = currentAbsence.type;
                const absence = currentAbsence;

                const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
                    <div
                        ref={ref}
                        onClick={(e) => {
                            e.preventDefault();
                            onClick(e);
                        }}
                        style={{ width: '100%', height: '100%', cursor: 'pointer' }}
                    >
                        {children}
                    </div>
                ));

                return (
                    <td key={dateStr} colSpan={colSpan} className="has-absence" style={{ padding: '2px', verticalAlign: 'middle' }}>
                        <Dropdown style={{ width: '100%', height: '100%' }}>
                            <Dropdown.Toggle as={CustomToggle}>
                                <OverlayTrigger placement="top" overlay={<Tooltip>{title}<br />{currentAbsence.comments || ''}</Tooltip>}>
                                    <div style={style}>
                                        <div style={{
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            maxWidth: '100%',
                                            textAlign: 'left',
                                            lineHeight: '1.2'
                                        }}>
                                            {currentAbsence.type}
                                        </div>
                                    </div>
                                </OverlayTrigger>
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <Dropdown.Item as="button" onClick={() => this.props.openEditAbsenceModal(absence, true)}>{t("absences.viewAction")}</Dropdown.Item>
                                {(absence.source === 'workflow' && absence.status !== wfStatus.denied && absence.status !== wfStatus.canceled) && (
                                    <Dropdown.Item
                                        as="button"
                                        onClick={() => this.props.handleCancelRequest(absence)}
                                    >
                                        {t("absences.cancelRequest")}
                                    </Dropdown.Item>
                                )}
                                {(absence.source === 'workflow' && absence.status !== wfStatus.pending) && (
                                    <Dropdown.Item
                                        as="button"
                                        onClick={() => this.props.handleReopenRequest(absence)}
                                    >
                                        {t("absences.reopenRequest")}
                                    </Dropdown.Item>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                    </td>
                );
            }

            return <td key={dateStr}></td>;
        });
    }

    handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollTop + clientHeight >= scrollHeight - 100) {
            if (!this.state.loading && this.state.hasMore) {
                this.loadMoreData();
            }
        }
    }

    render() {
        const { t, year, month } = this.props;
        const { displayedEmployees } = this.state;
        const days = this.getDaysInMonth();

        const monthDate = moment([year, month - 1]);
        const headerTitle = monthDate.format('MMMM YYYY');
        const capitalizedTitle = headerTitle.charAt(0).toUpperCase() + headerTitle.slice(1);

        return (
            <div className="team-absences-grid" style={{ position: 'relative' }}>

                {(!displayedEmployees || displayedEmployees.length === 0) ? (
                    <div className="text-center p-5">
                        {this.state.loading ? <Loading /> : <p className="h4 text-muted">{t("absences.noTeamMembers")}</p>}
                    </div>
                ) : (
                    <div
                        className="table-responsive"
                        style={{ maxHeight: 'calc(100vh - 200px)' }}
                        onScroll={this.handleScroll}
                        ref={this.scrollRef}
                    >
                        <table className="table table-bordered table-sm team-calendar-table" style={{ tableLayout: 'fixed', minWidth: '100%' }}>
                            <thead>
                                <tr style={{ height: '40px' }}>
                                    <th style={{
                                        verticalAlign: 'middle',
                                        minWidth: '176px',
                                        maxWidth: '176px',
                                        width: '176px',
                                        height: '40px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        position: 'sticky',
                                        left: 0,
                                        top: 0,
                                        background: 'white',
                                        zIndex: 20,
                                        boxShadow: 'inset -1px -1px 0 #dee2e6',
                                        fontSize: "14px",
                                        fontWeight: "500"
                                    }}>
                                        {capitalizedTitle}
                                    </th>
                                    {days.map(d => {
                                        const dayName = d.format('ddd').replace('.', '');
                                        const formattedDay = dayName.charAt(0).toUpperCase() + dayName.slice(1) + ' ' + d.format('D');
                                        return (
                                            <th key={d.format('DD')} className="text-center" style={{ minWidth: '80px', width: '80px', maxWidth: '80px', height: '40px', position: 'sticky', top: 0, background: 'white', zIndex: 15, verticalAlign: 'middle', boxShadow: 'inset 0 -1px 0 #dee2e6' }}>
                                                <div className="day-header" style={{ fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap' }}>{formattedDay}</div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {displayedEmployees.map((emp, idx) => (
                                    <tr key={emp?.id === undefined || emp?.id === null ? `emp-row-${idx}` : String(emp.id)} style={{ height: '40px' }}>
                                        <td style={{
                                            minWidth: '176px',
                                            maxWidth: '176px',
                                            width: '176px',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            position: 'sticky',
                                            left: 0,
                                            background: 'white',
                                            zIndex: 10,
                                            verticalAlign: 'middle',
                                            padding: '9px 16px',
                                            boxShadow: 'inset -1px 0 0 #dee2e6'
                                        }}>
                                            <div
                                                className="employee-cell"
                                                onClick={() => this.props.onEmployeeSelect(emp)}
                                                style={{ cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
                                            >
                                                <div className="employee-avatar-wrapper" style={{ width: '32px', height: '32px', marginRight: '8px', flexShrink: 0 }}>
                                                    <Avatar userId={emp.id} className="avatar-small" style={{ width: '100%', height: '100%', borderRadius: '50%', margin: "0" }} />
                                                </div>
                                                <div className="employee-text" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', justifyContent: 'center' }}>
                                                    <span className="employee-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: '1.2' }}>
                                                        {emp.name} {emp.surname}
                                                    </span>
                                                    <span className="employee-position" style={{ fontSize: '12px', color: '#667085', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: '1.2', fontWeight: 'normal' }}>
                                                        {emp.position || ''}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        {this.renderRowCells(emp, days)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {this.state.loading && (
                            <div className="p-3 text-center">
                                <Loading />
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}

export default withTranslation()(TeamAbsences);
